import each from 'lodash/each';
import filter from 'lodash/filter';
import find from 'lodash/find';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import map from 'lodash/map';

import {
  NodeSize,
  TreeLayoutNodeTypeEnum,
  START_NODE_ID,
} from 'src/presentation/components/pages/ivr/constants/ivrTreeConstants';
import { IvrLink, IvrNode } from 'src/presentation/components/pages/ivr/components/ivrTree/ivrTypes';
import { calculateLinksPaths } from 'src/presentation/components/pages/ivr/components/ivrTree/helpers/linksHelper';
import TreeNode from './TreeNode';
import { getNodesRect } from 'src/presentation/components/pages/ivr/components/ivrTree/helpers/nodesSettingsHelper';

export const StartNode = {
  ivrNode: { id: START_NODE_ID },
  height: NodeSize.StartNode.height,
  id: START_NODE_ID,
  name: 'start',
  nodeType: TreeLayoutNodeTypeEnum.START,
  type: 'START',
  width: NodeSize.StartNode.width,
};

export default class TreeData {
  private _tree: IvrNode[] = [];
  private _links: IvrLink[] = [];
  private _rootNode: IvrNode;
  private _abandonedNodes: IvrNode[] = [];
  private _abandonedNodesIds: string[] = [];
  private nodesMap: { [key: string]: IvrNode } = {};
  private _hierarchy: any;

  constructor(tree: IvrNode[], nodesHierarchy: any, rootNode?: IvrNode) {
    this._tree = tree || [];
    this._rootNode = rootNode;
    this._hierarchy = nodesHierarchy;
    this.nodesMap = this.buildMap();
  }

  get tree() {
    return this._tree;
  }

  get links() {
    return this._links;
  }

  set links(value) {
    this._links = value;
  }

  recalculateLinks(node?: TreeNode) {
    return calculateLinksPaths(this._links, this._tree, node);
  }

  get rootNode() {
    return this._rootNode;
  }

  get abandonedNode() {
    return this._abandonedNodes;
  }

  set abandonedNode(value) {
    this._abandonedNodes = value;
    this._abandonedNodesIds = map(this._abandonedNodes, node => node.id);
    this.nodesMap = this.buildMap();
  }

  get abandonedNodesIds() {
    return this._abandonedNodesIds;
  }

  get hierarchy() {
    return this._hierarchy;
  }

  buildMap() {
    return [StartNode, ...this._tree, ...this._abandonedNodes].reduce((acc: any, node: IvrNode) => {
      acc[node.id] = node;
      return acc;
    }, {});
  }

  findNodeById(id: string): TreeNode {
    return this.nodesMap[id];
  }

  getRect() {
    return getNodesRect(this._tree);
  }

  getParents(id: string): undefined | TreeNode[] {
    const nodesIds: any[] = filter(Object.keys(this._hierarchy), key =>
      find(this._hierarchy[key], (node: any) => node.id === id)
    );
    return !isEmpty(nodesIds) ? map(nodesIds, nodeId => this.findNodeById(nodeId)) : undefined;
  }

  getAllAncestors(id: string, ids: Set<string> = new Set()): Set<string> {
    const parents = this.getParents(id);

    if (!isEmpty(parent)) {
      each(parents as TreeNode[], (node: any) => {
        if (node.id === START_NODE_ID) {
          ids.add(START_NODE_ID);
          return ids;
        }

        if (node && !ids.has(node.id)) {
          ids.add(node.id);
          this.getAllAncestors(node.id, ids);
        }
      });
    }
    return ids;
  }

  getAllChildren(nodeId: string) {
    const ids = this.getAllChildrenIds(nodeId);
    return map(ids, id => this.nodesMap[id]).filter(node => !!node);
  }

  private getAllChildrenIds(nodeId: string, children: undefined | string[] = []) {
    const childList = get(this.hierarchy, nodeId);
    if (isEmpty(childList)) {
      return [];
    }

    each(childList, child => {
      if (nodeId !== child.id && !children.includes(child.id) && !this._abandonedNodesIds.includes(child.id)) {
        children.push(child.id);
        this.getAllChildrenIds(child.id, children);
      }
    });

    return children;
  }
}
